import React from 'react';
import { API_URL } from '../config';
import type { BrowserStreamState, BrowserStreamEvent, PlannerActivityEvent } from '@chatsdet/types';

type PlannerActivity = PlannerActivityEvent['data'];

/**
 * Merge a planner activity event into the feed. Upsert by `callId` so a tool
 * call's running→completed updates collapse into one row (in place) rather
 * than stacking duplicates. The in-place branch skips the `maxActions` cap —
 * it replaces an existing row, so it can't grow the list; only a new `callId`
 * appends and therefore needs to evict. Pure + exported for testing.
 */
export function upsertPlannerActivity(
  list: PlannerActivity[],
  item: PlannerActivity,
  maxActions: number
): PlannerActivity[] {
  const idx = list.findIndex((a) => a.callId === item.callId);
  if (idx >= 0) {
    const next = list.slice();
    next[idx] = item;
    return next;
  }
  return [...list.slice(-(maxActions - 1)), item];
}

const initialState: BrowserStreamState = {
  status: 'idle',
  latestScreenshot: null,
  latestAction: null,
  actions: [],
  reasoning: '',
  reasoningChunks: [],
  latestThought: null,
  cacheStatus: null,
  phase: null,
  videoStream: null,
  loginWorkflowCreated: null,
  assertionResults: [],
  assertionSummary: null,
  contextFindings: [],
  testEnd: null,
  currentUrl: null,
  plannerActivity: [],
};

/** Base reconnect backoff (ms). Doubles each consecutive failed attempt. */
const RECONNECT_BASE_MS = 1000;
/** Cap on the reconnect backoff (ms). */
const RECONNECT_MAX_MS = 10000;
/**
 * Consecutive failed reconnects before giving up and surfacing `error`. The
 * counter resets to 0 on every successful `onopen`, so this only trips when the
 * endpoint is genuinely unreachable for a sustained stretch — not during a long,
 * occasionally-flaky run.
 */
const MAX_RECONNECT_ATTEMPTS = 10;
/**
 * If no event arrives within this window while we believe the stream is open,
 * the connection is treated as silently stalled (a black-holed SSE that never
 * fires `onerror`) and force-reconnected. The server sends a `: keepalive`
 * comment every 5s, but native EventSource does not surface comments to JS, so
 * this watchdog relies on the run's own event traffic (screenshots, thoughts,
 * actions) plus a generous threshold.
 */
const STALE_STREAM_MS = 20000;
/** Watchdog poll cadence (ms). */
const WATCHDOG_INTERVAL_MS = 5000;

type UseBrowserStreamOptions = {
  /** Maximum number of actions to keep in history. Default: 100 */
  maxActions?: number;
  /** Auto-connect when streamId is provided. Default: true */
  autoConnect?: boolean;
};

type UseBrowserStreamReturn = {
  /** Current stream state */
  state: BrowserStreamState;
  /** Whether the stream is connected */
  isConnected: boolean;
  /** Manually connect to the stream */
  connect: () => void;
  /** Manually disconnect from the stream */
  disconnect: () => void;
  /** Reset the state to initial values */
  reset: () => void;
};

/**
 * Hook for subscribing to a browser stream via SSE.
 *
 * @param streamId - The stream ID to subscribe to (typically a job or workflow run ID)
 * @param options - Optional configuration
 * @returns Stream state and control functions
 *
 * @example
 * ```tsx
 * const { state, isConnected } = useBrowserStream(workflowRunId);
 *
 * return (
 *   <BrowserStreamPanel
 *     state={state}
 *     className="h-full"
 *   />
 * );
 * ```
 */
export function useBrowserStream(
  streamId: string | null,
  options?: UseBrowserStreamOptions
): UseBrowserStreamReturn {
  const { maxActions = 100, autoConnect = true } = options ?? {};

  const [state, setState] = React.useState<BrowserStreamState>(initialState);
  const [isConnected, setIsConnected] = React.useState(false);
  const eventSourceRef = React.useRef<EventSource | null>(null);

  // Reconnection + stale-stream watchdog bookkeeping. Refs (not state) so the
  // watchdog/backoff logic reads live values without re-subscribing effects.
  const reconnectAttemptsRef = React.useRef(0);
  const reconnectTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEventAtRef = React.useRef<number>(0);
  /** ISO `emittedAt` of the newest event seen — sent as `?since=` on reconnect. */
  const lastEmittedAtRef = React.useRef<string | null>(null);
  /** True once we've logged that reconnection exhausted its budget (log once). */
  const exhaustionLoggedRef = React.useRef(false);
  /** Set once a real terminal (browser.test-end) arrives — suppresses reconnect. */
  const terminalRef = React.useRef(false);
  /** Set on an explicit disconnect() — suppresses reconnect. */
  const manualDisconnectRef = React.useRef(false);
  /** Stable indirection so scheduleReconnect can call the latest connect(). */
  const connectRef = React.useRef<() => void>(() => {});
  /** Stable indirection so the event listeners call the latest handleEvent. */
  const handleEventRef = React.useRef<(event: BrowserStreamEvent) => void>(() => {});
  /**
   * A newly opened browser stream can stay eventless while sandbox/browser
   * preparation is still underway. Only enable the silent-stall watchdog after
   * at least one real named event proves that browser activity has begun.
   */
  const hasObservedEventRef = React.useRef(false);

  const handleEvent = React.useCallback(
    (event: BrowserStreamEvent) => {
      setState((prev) => {
        switch (event.type) {
          case 'browser.screenshot':
            return {
              ...prev,
              latestScreenshot: event.data,
              currentUrl: event.data.url ?? prev.currentUrl,
            };

          case 'browser.action':
            return {
              ...prev,
              latestAction: event.data,
              actions: [...prev.actions.slice(-(maxActions - 1)), event.data],
              currentUrl: event.data.url ?? prev.currentUrl,
            };

          case 'browser.reasoning':
            return {
              ...prev,
              reasoning: prev.reasoning + event.data.delta,
              reasoningChunks: [...prev.reasoningChunks, event.data.delta],
            };

          case 'browser.thought':
            return {
              ...prev,
              latestThought: event.data.thought,
            };

          case 'browser.status':
            return {
              ...prev,
              status: event.data.status,
              currentUrl:
                event.data.status === 'recovered' && event.data.recoveredUrl
                  ? event.data.recoveredUrl
                  : prev.currentUrl,
            };

          case 'browser.cache':
            return {
              ...prev,
              cacheStatus: {
                enabled: event.data.enabled,
                replayAttempted: event.data.replayAttempted,
                replaySucceeded: event.data.replaySucceeded,
                actionCount: event.data.actionCount,
                error: event.data.error,
              },
            };

          case 'browser.phase':
            return {
              ...prev,
              phase: {
                phase: event.data.phase,
                message: event.data.message,
                progress: event.data.progress,
              },
              // Clear thought when entering a non-agent phase so ThoughtCard shows the phase message
              latestThought:
                event.data.phase !== 'agent' && event.data.phase !== 'idle'
                  ? null
                  : prev.latestThought,
            };

          case 'browser.video-ready':
            return {
              ...prev,
              videoStream: {
                streamId: event.data.streamId,
                codec: event.data.codec,
              },
            };

          case 'login.workflow-created':
            return {
              ...prev,
              loginWorkflowCreated: {
                workflowId: event.data.workflowId,
                workflowName: event.data.workflowName,
              },
            };

          case 'browser.assertion-result':
            return {
              ...prev,
              assertionResults: [...prev.assertionResults, event.data],
              assertionSummary: event.data.summary,
            };

          case 'context.finding':
            return {
              ...prev,
              contextFindings: [...prev.contextFindings, event.data],
            };

          case 'browser.test-end':
            // Authoritative terminal — the run is over. Suppress any reconnect
            // so we don't reopen a stream the server has intentionally ended.
            terminalRef.current = true;
            return {
              ...prev,
              testEnd: {
                outcome: event.data.outcome,
                message: event.data.message,
              },
            };

          case 'planner.activity':
            return {
              ...prev,
              plannerActivity: upsertPlannerActivity(prev.plannerActivity, event.data, maxActions),
            };

          default:
            return prev;
        }
      });
    },
    [maxActions]
  );

  const clearReconnectTimer = React.useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  // Schedule a reconnect with exponential backoff + jitter. No-op once a real
  // terminal arrived or the user disconnected. After MAX_RECONNECT_ATTEMPTS we
  // surface an 'error' badge but KEEP retrying at the capped backoff — never a
  // permanent dead-end while the run may still be live (the attempt counter
  // resets to 0 the moment a genuine event arrives, see the event listener).
  const scheduleReconnect = React.useCallback(() => {
    if (reconnectTimerRef.current) return; // already scheduled
    if (terminalRef.current || manualDisconnectRef.current) return;

    const attempt = reconnectAttemptsRef.current;
    const exhausted = attempt >= MAX_RECONNECT_ATTEMPTS;
    if (exhausted) {
      setState((prev) => (prev.status === 'disconnected' ? prev : { ...prev, status: 'error' }));
      if (!exhaustionLoggedRef.current) {
        exhaustionLoggedRef.current = true;
        console.warn(
          '[useBrowserStream] reconnect budget exhausted; continuing at capped backoff',
          { streamId, attempts: attempt }
        );
      }
    }
    const base = exhausted
      ? RECONNECT_MAX_MS
      : Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
    // Jitter de-syncs reconnect storms when many viewers drop at once.
    const delay = base + Math.random() * 0.3 * base;
    reconnectAttemptsRef.current = attempt + 1;
    console.info('[useBrowserStream] scheduling reconnect', {
      streamId,
      attempt,
      delayMs: Math.round(delay),
    });
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connectRef.current();
    }, delay);
  }, [streamId]);

  const connect = React.useCallback(() => {
    if (!streamId) return;

    // A fresh connect supersedes any pending reconnect and clears the manual
    // suppressor (the caller explicitly wants a live connection).
    manualDisconnectRef.current = false;
    clearReconnectTimer();

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Update status to connecting
    setState((prev) => ({ ...prev, status: 'connecting' }));
    lastEventAtRef.current = Date.now();

    // On reconnect, ask the server to skip events we've already rendered so the
    // cache replay doesn't double-append actions/reasoning/findings.
    const sinceQuery = lastEmittedAtRef.current
      ? `?since=${encodeURIComponent(lastEmittedAtRef.current)}`
      : '';

    // Create new EventSource connection
    const eventSource = new EventSource(
      `${API_URL}/browser-stream/${streamId}/stream${sinceQuery}`,
      { withCredentials: true }
    );

    eventSourceRef.current = eventSource;

    // Handle browser stream event types
    const eventTypes = [
      'browser.screenshot',
      'browser.action',
      'browser.reasoning',
      'browser.thought',
      'browser.status',
      'browser.cache',
      'browser.phase',
      'browser.video-ready',
      'login.workflow-created',
      'browser.assertion-result',
      'browser.test-end',
      'context.finding',
      'planner.activity',
    ];

    eventTypes.forEach((eventType) => {
      eventSource.addEventListener(eventType, (e) => {
        // Stamp arrival for the stale-stream watchdog (keepalive comments are
        // invisible to EventSource, so we track real named events).
        lastEventAtRef.current = Date.now();
        try {
          const data = JSON.parse((e as MessageEvent).data);
          hasObservedEventRef.current = true;
          // Advance the `since` cursor and clear the reconnect budget only on
          // genuine forward progress (a strictly newer event) — not on a cached
          // boundary event replayed after reconnect, which would otherwise let a
          // dead stream reset its backoff forever.
          const emittedAt = (data as { emittedAt?: unknown })?.emittedAt;
          if (
            typeof emittedAt === 'string' &&
            (lastEmittedAtRef.current === null || emittedAt > lastEmittedAtRef.current)
          ) {
            lastEmittedAtRef.current = emittedAt;
            reconnectAttemptsRef.current = 0;
            exhaustionLoggedRef.current = false;
          }
          handleEventRef.current({ type: eventType, data } as BrowserStreamEvent);
        } catch (err) {
          console.error(`Failed to parse ${eventType} event:`, err);
        }
      });
    });

    eventSource.onopen = () => {
      // A successful open restores 'connected', but does NOT reset the backoff —
      // only a real event proves the stream is alive (an open that then stays
      // silent is the stalled case the watchdog handles).
      lastEventAtRef.current = Date.now();
      setIsConnected(true);
      setState((prev) => ({ ...prev, status: 'connected' }));
    };

    eventSource.onerror = () => {
      setIsConnected(false);
      eventSource.close();
      if (eventSourceRef.current === eventSource) {
        eventSourceRef.current = null;
      }

      // Real end (test-end) or user-initiated close — do not reconnect.
      if (terminalRef.current || manualDisconnectRef.current) {
        return;
      }

      // Transient drop: keep the badge on 'connecting' (we're retrying) unless a
      // graceful 'disconnected' status already set it, then back off + retry.
      setState((prev) =>
        prev.status === 'disconnected' ? prev : { ...prev, status: 'connecting' }
      );
      scheduleReconnect();
    };
  }, [streamId, clearReconnectTimer, scheduleReconnect]);

  // Keep refs pointed at the latest closures so the event listeners,
  // scheduleReconnect, and the watchdog use current values without making
  // `connect` depend on `handleEvent` (which would reconnect on a maxActions
  // change) or re-subscribing effects on every render.
  React.useEffect(() => {
    handleEventRef.current = handleEvent;
  }, [handleEvent]);

  React.useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const disconnect = React.useCallback(() => {
    manualDisconnectRef.current = true;
    clearReconnectTimer();
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
    setState((prev) => ({ ...prev, status: 'disconnected' }));
  }, [clearReconnectTimer]);

  const reset = React.useCallback(() => {
    setState(initialState);
  }, []);

  // Auto-connect when streamId changes (if autoConnect is enabled). Reset the
  // per-stream reconnect/terminal bookkeeping for the new stream.
  React.useEffect(() => {
    terminalRef.current = false;
    manualDisconnectRef.current = false;
    reconnectAttemptsRef.current = 0;
    exhaustionLoggedRef.current = false;
    lastEmittedAtRef.current = null;
    hasObservedEventRef.current = false;

    if (streamId && autoConnect) {
      connect();
    }

    return () => {
      clearReconnectTimer();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [streamId, autoConnect, connect, clearReconnectTimer]);

  // Stale-stream watchdog: a black-holed SSE can stay readyState OPEN while
  // silently delivering nothing and never firing onerror. If no event arrives
  // within STALE_STREAM_MS while we believe the stream is open, force-reconnect.
  React.useEffect(() => {
    if (!streamId || !autoConnect) return;

    const interval = setInterval(() => {
      if (terminalRef.current || manualDisconnectRef.current) return;
      if (reconnectTimerRef.current) return; // a reconnect is already pending
      const es = eventSourceRef.current;
      if (!es || es.readyState !== EventSource.OPEN) return;
      // EventSource keepalive comments are invisible to JavaScript. Before the
      // first named browser event, silence is expected during run preparation,
      // so leave this connection alone until browser activity is observable.
      if (!hasObservedEventRef.current) return;
      const silentFor = Date.now() - lastEventAtRef.current;
      if (silentFor <= STALE_STREAM_MS) return;

      // Silent stall — recycle via the shared backoff path so a permanently
      // silent stream backs off instead of hammering every watchdog tick.
      console.warn('[useBrowserStream] stale stream detected; forcing reconnect', {
        streamId,
        silentForMs: silentFor,
      });
      scheduleReconnect();
    }, WATCHDOG_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [streamId, autoConnect, scheduleReconnect]);

  // Reset state when streamId clears
  React.useEffect(() => {
    if (!streamId) {
      setState(initialState);
      setIsConnected(false);
    }
  }, [streamId]);

  return {
    state,
    isConnected,
    connect,
    disconnect,
    reset,
  };
}
